/**
 * @fullvsl/app-shell/main — integrated MVP app shell entry (FUL-225/226).
 *
 * Boots the FullVSL app: injects the shell CSS, creates the VslStore + router,
 * renders the sidebar/topbar shell (approved design system: 240px sidebar,
 * topbar with search, operational SaaS pattern), and dispatches to the correct
 * view on each route change.
 *
 * Design: FullVSL design system (teal #00685f primary, Inter, light surface).
 * Iframe-safe: hash-based routing, no top-window access, no third-party cookies,
 * localStorage with graceful degradation when storage is partitioned/blocked.
 */
import { SHELL_CSS } from "./styles";
import { VslStore } from "./store";
import { createRouter } from "./router";
import type { Route } from "./router";
import { el, append, icon, ICONS } from "./dom";
import { renderListView } from "./views/list";
import { renderEditorView } from "./views/editor";
import { renderPreviewView } from "./views/preview";
import { renderAnalyticsView } from "./views/analytics";
import { renderPublishView } from "./views/publish";
import { renderIntegrationsView } from "./views/integrations";
import { renderAnalyticsOverviewView } from "./views/analytics-overview";
import { renderTemplatesView } from "./views/templates";
import { renderSupportView } from "./views/support";

let cssInjected = false;
function injectCss(): void {
  if (cssInjected) return;
  cssInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-fullvsl-app", "");
  style.textContent = SHELL_CSS;
  document.head.appendChild(style);
}

function boot(): void {
  injectCss();

  const store = new VslStore();
  const router = createRouter();
  const appRoot = document.getElementById("app");
  if (!appRoot) return;

  // FUL-244: hydrate from the backend API when it's available (same-origin).
  // Views re-render on the store change event once hydration completes.
  store.initLive().then((live) => {
    if (live) console.info("[FullVSL] live mode: hydrated from backend API");
  });

  // ---- Shell skeleton ----
  const shell = el("div", "fva-shell");

  // Sidebar backdrop (mobile)
  const backdrop = el("div", "fva-sidebar-backdrop");

  // ---- Sidebar ----
  const sidebar = el("aside", "fva-sidebar");

  // Brand block
  const brand = el("div", "fva-brand");
  const brandLogo = el("div", "fva-brand-logo");
  brandLogo.appendChild(icon(ICONS.videoLibrary, "fva-nav-icon"));
  const brandText = el("div");
  append(brandText, el("div", "fva-brand-name", "FullVSL"));
  append(brandText, el("div", "fva-brand-sub", "Suíte Operacional"));
  append(brand, brandLogo, brandText);
  append(sidebar, brand);

  // Nova VSL CTA
  const navCta = el("button", "fva-nav-cta");
  append(navCta, icon(ICONS.plus, "fva-nav-icon"), el("span", null, "Nova VSL"));
  navCta.addEventListener("click", () => router.navigate("#/new"));
  append(sidebar, navCta);

  // Primary nav
  const nav = el("nav", "fva-nav");
  const navAll = makeNavItem(ICONS.videoLibrary, "VSLs", "#/", () => router.current().name === "list" || router.current().name === "create" || router.current().name === "editor" || router.current().name === "preview" || router.current().name === "publish");
  const navAnalytics = makeNavItem(ICONS.analytics, "Estatísticas", "#/analytics", () => router.current().name === "analyticsOverview" || router.current().name === "analytics");
  const navTemplates = makeNavItem(ICONS.templates, "Modelos de overlay", "#/templates", () => router.current().name === "templates");
  const navIntegrations = makeNavItem(ICONS.extension, "Integrações", "#/integrations", () => router.current().name === "integrations");
  append(nav, navAll, navAnalytics, navTemplates, navIntegrations);
  append(sidebar, nav);

  // Sidebar footer
  const sidebarFooter = el("div", "fva-sidebar-footer");
  const supportItem = makeNavItem(ICONS.help, "Suporte", "#/support", () => router.current().name === "support");
  const signOutItem = makeNavItem(ICONS.logout, "Sair", "#/signout", () => false);
  append(sidebarFooter, supportItem, signOutItem);
  append(sidebar, sidebarFooter);

  // ---- Main area ----
  const main = el("div", "fva-main");

  // Topbar
  const topbar = el("div", "fva-topbar");
  const menuBtn = el("button", "fva-menu-btn", "☰");
  menuBtn.setAttribute("aria-label", "Abrir ou fechar navegação");
  menuBtn.addEventListener("click", () => {
    sidebar.classList.toggle("is-open");
    backdrop.classList.toggle("is-visible");
  });
  backdrop.addEventListener("click", () => {
    sidebar.classList.remove("is-open");
    backdrop.classList.remove("is-visible");
  });

  // Search bar
  const searchWrap = el("div", "fva-topbar-search");
  searchWrap.appendChild(icon(ICONS.search, "fva-search-icon"));
  const searchInput = el("input") as HTMLInputElement;
  searchInput.type = "text";
  searchInput.placeholder = "Pesquisar VSLs…";
  searchInput.setAttribute("aria-label", "Pesquisar");
  searchWrap.appendChild(searchInput);

  const topbarActions = el("div", "fva-topbar-actions");
  const notifBtn = el("button", "fva-icon-btn");
  notifBtn.setAttribute("aria-label", "Notificações");
  notifBtn.appendChild(icon(ICONS.notifications, "fva-nav-icon"));
  append(notifBtn, el("span", "fva-icon-btn-dot"));
  const avatar = el("div", "fva-avatar", "MR");
  append(topbarActions, notifBtn, avatar);

  append(topbar, menuBtn, searchWrap, topbarActions);

  const content = el("div", "fva-content");
  const contentInner = el("div", "fva-content-inner");
  content.appendChild(contentInner);
  append(main, topbar, content);

  append(shell, sidebar, main);
  appRoot.innerHTML = "";
  appRoot.appendChild(shell);
  appRoot.appendChild(backdrop);

  // ---- View rendering ----
  let cleanup: (() => void) | null = null;

  function renderView(route: Route): void {
    // Cleanup previous view
    if (cleanup) {
      cleanup();
      cleanup = null;
    }

    contentInner.innerHTML = "";

    // FUL-272: error boundary. If any view throws during render, show a
    // recoverable error state instead of leaving the main canvas blank (the
    // exact P0 symptom). The shell + nav stay usable so the viewer can recover.
    try {
      dispatchRoute(route);
    } catch (err) {
      cleanup = null;
      contentInner.innerHTML = "";
      const errorState = el("div", "fva-empty");
      append(errorState, el("h3", null, "Algo deu errado"));
      append(errorState, el("p", null, "Não foi possível exibir esta tela. Volte ao painel e tente novamente."));
      const retryBtn = el("button", "fva-btn fva-btn-primary", "Voltar ao painel") as HTMLButtonElement;
      retryBtn.addEventListener("click", () => router.navigate("#/"));
      append(errorState, retryBtn);
      console.error("[FullVSL] view render failed:", err);
      contentInner.appendChild(errorState);
    }

    // Close mobile sidebar on navigation
    sidebar.classList.remove("is-open");
    backdrop.classList.remove("is-visible");
  }

  /** Render the DOM for a resolved route. May throw — callers wrap in a boundary. */
  function dispatchRoute(route: Route): void {
    switch (route.name) {
      case "list":
        contentInner.appendChild(renderListView(store, (p) => router.navigate(p)));
        break;

      case "create":
        contentInner.appendChild(
          renderEditorView({ mode: "create", store, navigate: (p) => router.navigate(p) }),
        );
        break;

      case "editor":
        contentInner.appendChild(
          renderEditorView({
            mode: "edit",
            store,
            projectId: route.id,
            navigate: (p) => router.navigate(p),
          }),
        );
        break;

      case "preview": {
        const result = renderPreviewView(store, route.id, (p) => router.navigate(p));
        contentInner.appendChild(result.mount);
        cleanup = result.cleanup;
        break;
      }

      case "analytics": {
        const result = renderAnalyticsView(store, route.id, (p) => router.navigate(p));
        contentInner.appendChild(result.mount);
        cleanup = result.cleanup;
        break;
      }

      case "publish": {
        const result = renderPublishView(store, route.id, (p) => router.navigate(p));
        contentInner.appendChild(result.mount);
        cleanup = result.cleanup;
        break;
      }

      case "integrations": {
        const result = renderIntegrationsView();
        contentInner.appendChild(result.mount);
        cleanup = result.cleanup;
        break;
      }

      case "analyticsOverview": {
        contentInner.appendChild(renderAnalyticsOverviewView(store, (p) => router.navigate(p)));
        break;
      }

      case "templates": {
        contentInner.appendChild(renderTemplatesView((p) => router.navigate(p)));
        break;
      }

      case "support": {
        contentInner.appendChild(renderSupportView());
        break;
      }

      case "signout": {
        const wrap = el("div", "fva-empty");
        append(wrap, el("h3", null, "Sessão encerrada"));
        append(wrap, el("p", null, "Sua sessão local foi encerrada. Volte ao painel para continuar."));
        const returnBtn = el("button", "fva-btn fva-btn-primary", "Voltar ao painel") as HTMLButtonElement;
        returnBtn.addEventListener("click", () => router.navigate("#/"));
        append(wrap, returnBtn);
        contentInner.appendChild(wrap);
        break;
      }

      default: {
        const notFound = el("div", "fva-empty");
        append(notFound, el("h3", null, "Página não encontrada"));
        const homeLink = el("button", "fva-btn fva-btn-primary", "Ir para o painel") as HTMLButtonElement;
        homeLink.addEventListener("click", () => router.navigate("#/"));
        append(notFound, homeLink);
        contentInner.appendChild(notFound);
      }
    }
  }

  // ---- Wire router ----
  router.subscribe(() => {
    renderView(router.current());
    updateNavActive();
  });

  // Initial render
  renderView(router.current());
  updateNavActive();

  // ---- Search wiring (client-side filter on the list view) ----
  searchInput.addEventListener("input", () => {
    const r = router.current();
    if (r.name !== "list") return;
    const query = searchInput.value.toLowerCase().trim();
    contentInner.querySelectorAll<HTMLTableRowElement>(".fva-table tbody tr").forEach((row) => {
      const text = row.textContent?.toLowerCase() ?? "";
      row.style.display = query && !text.includes(query) ? "none" : "";
    });
  });

  function updateNavActive(): void {
    const r = router.current();
    const isActive = (path: string) => {
      if (path === "#/") {
        return r.name === "list" || r.name === "create" || r.name === "editor" || r.name === "preview" || r.name === "publish";
      }
      if (path === "#/analytics") {
        return r.name === "analyticsOverview" || r.name === "analytics";
      }
      if (path === "#/templates") return r.name === "templates";
      if (path === "#/integrations") return r.name === "integrations";
      if (path === "#/support") return r.name === "support";
      return false;
    };
    sidebar.querySelectorAll<HTMLButtonElement>(".fva-nav-item").forEach((item) => {
      item.classList.toggle("is-active", isActive(item.dataset.path ?? ""));
    });
  }
}

function makeNavItem(
  iconPath: string,
  label: string,
  path: string,
  isActive: () => boolean,
): HTMLElement {
  const item = el("button", "fva-nav-item");
  item.dataset.path = path;
  append(item, icon(iconPath), el("span", null, label));
  item.addEventListener("click", () => {
    // FUL-272: every sidebar menu item navigates to a real route. No menu
    // link is a dead-end "visual placeholder".
    location.hash = path;
  });
  item.classList.toggle("is-active", isActive());
  return item;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
